import { useRollbar } from '@rollbar/react'
import { useCallback, useState } from 'react'
import { useSWRConfig } from 'swr'
import { getPageCommentsKey, usePageComments } from 'modules/comments/api/get-page-comments'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import {
  handleTargetComment,
  parseParentIdFromKey,
} from 'modules/comments/components/comments-list/utils'
import { CommentSortEnum, CommentsTypeEnum } from 'modules/comments/types'
import { getCommentSortPreference } from 'modules/comments/utils/comment-preferences'
import { getDefaultSortOption } from 'modules/comments/utils/comment-sorting'
import { mergeComments } from 'modules/comments/utils/merge-comments'

interface UseCommentsWithCacheManagementProps {
  commentsType: CommentsTypeEnum
  pageId: number
  shouldFetch?: boolean
  onError?: (error: unknown) => void
  targetCommentId?: number
  isCommunityComments: boolean
  setHighlightedComment: (comment: CommentInterface | null) => void
  highlightedComment: CommentInterface | null
}

interface UpdateRepliesProps {
  commentId: number
  newReplies?: CommentInterface[]
}

export const useCommentsWithCacheManagement = ({
  commentsType,
  pageId,
  shouldFetch = true,
  onError,
  targetCommentId,
  isCommunityComments,
  setHighlightedComment,
  highlightedComment,
}: UseCommentsWithCacheManagementProps) => {
  const rollbar = useRollbar()
  const { mutate } = useSWRConfig()

  const [sortOption, setSortOption] = useState<CommentSortEnum>(() => {
    const savedPreference = getCommentSortPreference(rollbar.warning)
    return savedPreference || getDefaultSortOption(isCommunityComments)
  })
  const [initialTargetCommentId, setInitialTargetCommentId] = useState<number | undefined>(
    targetCommentId,
  )
  const [comments, setComments] = useState<CommentInterface[]>([])
  const [replies, setReplies] = useState<{
    [parentCommentId: number]: { replies: CommentInterface[] }
  }>({})
  const [requestParams, setRequestParams] = useState<{
    lastCommentId: number
    parentId: number | null
  }>({ lastCommentId: 0, parentId: null })

  const updateReplies = useCallback(({ commentId, newReplies }: UpdateRepliesProps) => {
    setReplies(prev => {
      const prevComment = prev[commentId] || {
        replies: [] as CommentInterface[],
      }
      return {
        ...prev,
        [commentId]: {
          replies: newReplies ?? prevComment.replies,
        },
      }
    })
  }, [])

  const getPageCommentsQuery = usePageComments({
    parentId: requestParams.parentId,
    commentsType,
    pageId,
    lastCommentId: requestParams.lastCommentId,
    targetCommentId:
      requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
        ? initialTargetCommentId
        : undefined,
    sortOption,
    config: {
      revalidateOnMount: shouldFetch,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data, key) => {
        const { isReply, parentId } = parseParentIdFromKey(key)

        if (isReply) {
          updateReplies({
            commentId: parentId,
            newReplies: mergeComments([...(replies[parentId]?.replies ?? [])], data ?? []),
          })
        } else {
          setComments(prev => {
            if (requestParams.lastCommentId === 0) {
              return data ?? []
            }
            return mergeComments(prev, data ?? [])
          })
          data?.forEach(comment => {
            if (comment.children) {
              updateReplies({
                commentId: comment.id,
                newReplies: mergeComments(
                  [...(replies[comment.id]?.replies ?? [])],
                  comment.children,
                ),
              })
            }
          })
        }

        handleTargetComment(
          data,
          initialTargetCommentId,
          setHighlightedComment,
          highlightedComment,
          setInitialTargetCommentId,
        )
      },
      onError: (error, key) => {
        const { isReply, parentId } = parseParentIdFromKey(key)

        if (isReply) {
          updateReplies({ commentId: parentId, newReplies: [] })
        }

        onError && onError(error)
      },
    },
  })

  const clearCacheAndRefresh = useCallback(
    (newSortOption: CommentSortEnum) => {
      const oldCacheKey = getPageCommentsKey({
        parentId: requestParams.parentId,
        commentsType,
        pageId,
        lastCommentId: requestParams.lastCommentId,
        sortOption,
        targetCommentId:
          requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
            ? initialTargetCommentId
            : undefined,
      })

      const newRequestParams = { lastCommentId: 0, parentId: null }
      setRequestParams(newRequestParams)
      setComments([])
      setReplies({})

      const newCacheKey = getPageCommentsKey({
        parentId: newRequestParams.parentId,
        commentsType,
        pageId,
        lastCommentId: newRequestParams.lastCommentId,
        sortOption: newSortOption,
        targetCommentId:
          newRequestParams.lastCommentId === 0 &&
          !newRequestParams.parentId &&
          !!initialTargetCommentId
            ? initialTargetCommentId
            : undefined,
      })

      mutate(oldCacheKey, undefined, { revalidate: false })
      mutate(newCacheKey, undefined, { revalidate: true })
    },
    [
      requestParams.parentId,
      requestParams.lastCommentId,
      commentsType,
      pageId,
      sortOption,
      initialTargetCommentId,
      mutate,
    ],
  )

  const handleSortChange = useCallback(
    (newSortOption: CommentSortEnum) => {
      if (newSortOption === sortOption) return

      setSortOption(newSortOption)
      clearCacheAndRefresh(newSortOption)
    },
    [sortOption, clearCacheAndRefresh],
  )

  const handleLoadMoreGap = useCallback(
    ({ lastCommentId, parentId }: { lastCommentId: number; parentId: number | null }) => {
      if (parentId) {
        updateReplies({ commentId: parentId })
        setRequestParams({ lastCommentId, parentId })
      } else {
        setRequestParams({ lastCommentId, parentId: null })
      }
    },
    [updateReplies],
  )

  return {
    comments,
    replies,
    sortOption,
    requestParams,
    updateReplies,
    handleSortChange,
    handleLoadMoreGap,
    getPageCommentsQuery,
    setComments,
  }
}
